import React from "react";
import { connect } from "react-redux";
import { Checkbox, Card, CardHeader, CardText } from "material-ui";
import { Link } from "react-router-dom";
import moment from "moment";
import PropTypes from "prop-types";
import { commitMutation, graphql } from "react-relay";
import { StyleSheet, css } from "aphrodite";

import environment from "../../relay/environment";
import OurismanTravelTheme from "../../ourismanTravelTheme";
import Notes from "../NotesComponent";

const TripList = ({ trips }) => (
  <div>
    <h3>Trips ({trips.length})</h3>
    <ul>
      {trips.map(({ id, tripName, owner: { firstName } }) => (
        <li key={id}>
          <Link to={`/trips/${id}/`}>
            {tripName} ({firstName})
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

TripList.propTypes = {
  trips: PropTypes.arrayOf(PropTypes.object).isRequired
};

const ResponseList = ({ responses }) => (
  <div>
    <h3>Form Responses ({responses.length})</h3>
    <ul>
      {responses.map(({ node: { id, display } }) => (
        <li key={id}>
          <Link to={`/forms/responses/${id}/`}>{display}</Link>
        </li>
      ))}
    </ul>
  </div>
);

ResponseList.propTypes = {
  responses: PropTypes.arrayOf(PropTypes.object).isRequired
};

@connect(state => ({
  viewport: state.Responsive.get("viewport")
}))
class ClientDetail extends React.Component {
  static propTypes = {
    client: PropTypes.shape().isRequired
  };

  static primaryTravelerInfo(primaryTraveler) {
    if (!primaryTraveler) {
      return <h4>primary traveler</h4>;
    }
    const { id, fullName } = primaryTraveler;

    return (
      <h4>
        <i>primary traveler</i>: <Link to={`/clients/${id}/`}>{fullName}</Link>
      </h4>
    );
  }

  setCheckbox = checkbox => (e, checked) => {
    const {
      client: { id: travelerId }
    } = this.props;
    const input = { travelerId, checkbox, value: checked };

    commitMutation(environment, {
      mutation: graphql`
        mutation ClientDetail_SetClientFlag_Mutation(
          $input: SetClientFlagInput!
        ) {
          setClientFlag(input: $input) {
            traveler {
              insideCircle
              lowPriority
              needsTraining
              needsVetting
              fired
              prospect
              acceptPolicies
              acceptPoliciesTimestamp
              highPriority
            }
          }
        }
      `,
      variables: { input },
      onError: err => alert(err)
    });
  };

  triggerWelcomeCampaign = () => {
    const {
      client: { id: travelerId }
    } = this.props;
    const input = { travelerId };

    commitMutation(environment, {
      mutation: graphql`
        mutation ClientDetail_TriggerWelcomeCampaign_Mutation(
          $input: TriggerWelcomeCampaignInput!
        ) {
          triggerWelcomeCampaign(input: $input) {
            traveler {
              welcomeCampaign
            }
          }
        }
      `,
      variables: { input },
      onError: err => alert(err)
    });
  };

  triggerWelcomeBack = () => {
    const {
      client: { id: travelerId }
    } = this.props;
    const input = { travelerId };

    commitMutation(environment, {
      mutation: graphql`
        mutation ClientDetails_TriggerWecomeBack_Mutation(
          $input: TriggerWelcomeBackInput!
        ) {
          triggerWelcomeBack(input: $input) {
            traveler {
              welcomeBack
            }
          }
        }
      `,
      variables: { input },
      onError: err => alert(err)
    });
  };

  render() {
    const {
      client: {
        id,
        fullName,
        primaryTraveler,
        firstName,
        middleName,
        lastName,
        birthdate,
        currentTier,
        email,
        insideCircle,
        lowPriority,
        highPriority,
        needsTraining,
        needsVetting,
        fired,
        trips,
        prospect,
        displayName,
        responseSet: { edges: responses },
        acceptPolicies,
        acceptPoliciesTimestamp,
        profile,
        welcomeCampaign,
        welcomeBack,
        vipNotes,
        profileNotes,
        travelers
      }
    } = this.props;

    const tripList = trips.edges.map(edge => edge.node);
    const primaryTravelerLink = primaryTraveler ? (
      <Link to={`/clients/${primaryTraveler.id}/`}>
        {primaryTraveler.fullName}
      </Link>
    ) : (
      "(primary traveler)"
    );

    const momentBirthdate = moment(birthdate);
    const age = moment.duration(moment().diff(momentBirthdate)).years();
    const format = "MMMM Do YYYY";
    const formattedBirthdate = `${momentBirthdate.format(
      format
    )} (${age} years)`;
    const display = displayName || fullName;
    const profileData = JSON.parse(profile);

    const hasEmail = email && email.length > 0;

    const travelerList = travelers.edges.map(edge => edge.node);

    return (
      <div className={css(styles.clientContainer)}>
        <div className={css(styles.header)}>
          <h1 className={css(styles.headerName)}>
            {display}{" "}
            {acceptPolicies ? (
              <small>
                (accepted booking policies{" "}
                {moment(acceptPoliciesTimestamp).fromNow()})
              </small>
            ) : null}
          </h1>
          <span className={css(styles.headerPrimaryTraveler)}>
            {primaryTravelerLink}
          </span>
          {currentTier ? (
            <span className={css(styles.headerTier)}>Tier {currentTier}</span>
          ) : null}
        </div>
        <div className={css(styles.clientFlagContainer)}>
          <span className={css(styles.clientFlag)}>
            <Checkbox
              label="Inside Circle"
              checked={insideCircle}
              onCheck={this.setCheckbox("insideCircle")}
            />
          </span>
          <span className={css(styles.clientFlag)}>
            <Checkbox
              label="BMOC"
              checked={highPriority}
              onCheck={this.setCheckbox("highPriority")}
            />
          </span>
          <span className={css(styles.clientFlag)}>
            <Checkbox
              label="Low Priority"
              checked={lowPriority}
              onCheck={this.setCheckbox("lowPriority")}
            />
          </span>
          <span className={css(styles.clientFlag)}>
            <Checkbox
              label="Needs Training"
              checked={needsTraining}
              onCheck={this.setCheckbox("needsTraining")}
            />
          </span>
          <span className={css(styles.clientFlag)}>
            <Checkbox
              label="Needs Vetting"
              checked={needsVetting}
              onCheck={this.setCheckbox("needsVetting")}
            />
          </span>
          <span className={css(styles.clientFlag)}>
            <Checkbox
              label="Fired"
              checked={fired}
              onCheck={this.setCheckbox("fired")}
            />
          </span>
          <span className={css(styles.clientFlag)}>
            <Checkbox
              label="Prospect"
              checked={prospect}
              onCheck={this.setCheckbox("prospect")}
            />
          </span>
          <span className={css(styles.clientFlag)}>
            <Checkbox
              label="Accepted Booking Policies"
              checked={acceptPolicies}
              onCheck={this.setCheckbox("acceptPolicies")}
            />
          </span>
          {!insideCircle ? (
            <span className={css(styles.clientFlag)}>
              <Checkbox
                label="Welcome Campaign"
                checked={welcomeCampaign}
                disabled={welcomeCampaign || !hasEmail}
                onCheck={this.triggerWelcomeCampaign}
              />
            </span>
          ) : null}
          {!insideCircle ? (
            <span className={css(styles.clientFlag)}>
              <Checkbox
                label="Welcome Back Campaign"
                checked={welcomeBack}
                disabled={welcomeBack || !hasEmail}
                onCheck={this.triggerWelcomeBack}
              />
            </span>
          ) : null}
        </div>
        <div className={css(styles.dataTable)}>
          <div className={css(styles.dataRow)}>
            <span className={css(styles.dataLabel)}>First name:</span>
            <span className={css(styles.dataContent)}>{firstName}</span>
          </div>
          <div className={css(styles.dataRow)}>
            <span className={css(styles.dataLabel)}>Middle name:</span>
            <span className={css(styles.dataContent)}>{middleName}</span>
          </div>
          <div className={css(styles.dataRow)}>
            <span className={css(styles.dataLabel)}>Last name:</span>
            <span className={css(styles.dataContent)}>{lastName}</span>
          </div>
          <div className={css(styles.dataRow)}>
            <span className={css(styles.dataLabel)}>Display name:</span>
            <span className={css(styles.dataContent)}>{display}</span>
          </div>
          <div className={css(styles.dataRow)}>
            <span className={css(styles.dataLabel)}>Birthdate:</span>
            <span className={css(styles.dataContent)}>
              {formattedBirthdate}
            </span>
          </div>
          {email.map((address, index) => (
            <div className={css(styles.dataRow)} key={index}>
              <span className={css(styles.dataLabel)}>Email {index + 1}:</span>
              <span className={css(styles.dataContent)}>{address}</span>
            </div>
          ))}
        </div>
        <div>
          <h3>Traveler Profile</h3>
          <div className={css(styles.dataTable)}>
            <div className={css(styles.dataRow)}>
              <span className={css(styles.dataLabel)}>Smoking:</span>
              <span className={css(styles.dataContent)}>
                {profileData.smoking ? "smoking" : "non-smoking"}
              </span>
            </div>
            <div className={css(styles.dataRow)}>
              <span className={css(styles.dataLabel)}>Bedding:</span>
              <span className={css(styles.dataContent)}>
                {profileData.bedding}
              </span>
            </div>
            <div className={css(styles.dataRow)}>
              <span className={css(styles.dataLabel)}>Standard Requests:</span>
              <span className={css(styles.dataContent)}>
                {profileData.standardRequests}
              </span>
            </div>
            <div className={css(styles.dataRow)}>
              <span className={css(styles.dataLabel)}>Favorite Things:</span>
              <span className={css(styles.dataContent)}>
                {profileData.favoriteThings}
              </span>
            </div>
            <div className={css(styles.dataRow)}>
              <span className={css(styles.dataLabel)}>Need to Know:</span>
              <span className={css(styles.dataContent)}>
                {profileData.needToKnow}
              </span>
            </div>
            <div className={css(styles.dataRow)}>
              <span className={css(styles.dataLabel)}>Referred By:</span>
              <span className={css(styles.dataContent)}>
                {profileData.referral}
                {profileData.other ? `: ${profileData.other}` : null}
              </span>
            </div>
            <div className={css(styles.dataRow)}>
              <span className={css(styles.dataLabel)}>Other travelers:</span>
              <span className={css(styles.dataContent)}>
                {travelerList.length > 0 ? travelerList.length : "n/a"}
              </span>
            </div>
          </div>
        </div>
        <Card className={css(styles.notes)}>
          <CardHeader
            title="VIP Notes"
            subtitle="Exactly as will appear in VIP letters."
          />
          <CardText>
            <Notes id={id} fieldName="vipNotes" value={vipNotes} />
            {travelerList.length > 0 ? (
              <div>
                <div>VIP Notes for other travelers:</div>
                <div className={css(styles.dataTable)}>
                  {travelerList.map(traveler => (
                    <div className={css(styles.dataRow)}>
                      <span className={css(styles.dataLabel)}>
                        <Link to={`/clients/${traveler.id}/`}>
                          {traveler.displayName || traveler.fullName}
                        </Link>
                      </span>
                      {/* eslint-disable react/no-danger */}
                      <div
                        className="ql-editor"
                        dangerouslySetInnerHTML={{ __html: traveler.vipNotes }}
                      />
                      {/* eslint-enable react/no-danger */}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </CardText>
        </Card>
        <Card className={css(styles.notes)}>
          <CardHeader title="Profile Notes" />
          <CardText>
            <Notes id={id} fieldName="profileNotes" value={profileNotes} />
          </CardText>
        </Card>
        {responses.length > 0 ? <ResponseList responses={responses} /> : null}
        {tripList.length > 0 ? <TripList trips={tripList} /> : null}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  clientContainer: {
    paddingLeft: 5,
    paddingRight: 5
  },
  header: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 15
  },
  headerName: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 5
  },
  headerPrimaryTraveler: {
    marginLeft: 15,
    fontSize: 12
  },
  headerTier: {},
  clientFlagContainer: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap"
  },
  clientFlag: {
    flex: "0 1 20%"
  },
  dataTable: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginBottom: 10
  },
  dataRow: {
    ":nth-child(even)": {
      background: OurismanTravelTheme.palette.borderColor
    },
    display: "flex",
    alignItems: "flex-start"
  },
  dataLabel: {
    fontSize: 12,
    width: "15%"
  },
  dataContent: {
    width: "85%"
  },
  notes: {
    marginBottom: 5
  }
});

export default ClientDetail;



// WEBPACK FOOTER //
// /src/components/Clients/ClientDetail.js